import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';

import { DATA_SOURCE_REIMPORT_FROM_S3_XML_PREVIEW } from 'lib/networking/endpoints';
import usePost from 'lib/hooks/usePost';

// Re-import a manufacturer's GDSN products from the stored S3 tradeItem XMLs.
// Shows the planner's preview (to-import / withdrawn / superseded) before confirming.
function GDSNReimportFromS3Modal({ show, onHide, manufacturer, onConfirm, loading }) {
  const [selectedGlns, setSelectedGlns] = useState([]);
  const [excludeWithdrawn, setExcludeWithdrawn] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const { postData: fetchPreview, loading: previewLoading } = usePost(
    DATA_SOURCE_REIMPORT_FROM_S3_XML_PREVIEW,
  );

  useEffect(() => {
    if (manufacturer?.manufacturer_glns && Object.keys(manufacturer.manufacturer_glns).length > 0) {
      setSelectedGlns([Object.keys(manufacturer.manufacturer_glns)[0]]);
    } else {
      setSelectedGlns([]);
    }
  }, [manufacturer]);

  useEffect(() => {
    if (!show || !manufacturer || selectedGlns.length === 0) {
      setPreview(null);
      setPreviewError(null);
      return undefined;
    }
    let cancelled = false;
    setPreviewError(null);
    // Debounce so rapid GLN toggles don't fire a (potentially slow) S3-listing preview each time.
    const timer = setTimeout(() => {
      fetchPreview({
        manufacturer_id: manufacturer.id,
        manufacturer_glns: selectedGlns,
        exclude_withdrawn: excludeWithdrawn,
      }).then(res => {
        if (cancelled) return;
        if (res?.data?.counts) {
          setPreview(res.data);
          setPreviewError(null);
        } else {
          setPreview(null);
          setPreviewError(res?.response?.data?.message || 'Could not build the re-import plan.');
        }
      });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, manufacturer, selectedGlns, excludeWithdrawn]);

  const handleGlnChange = gln => {
    setSelectedGlns(prev => (prev.includes(gln) ? prev.filter(g => g !== gln) : [...prev, gln]));
  };

  const handleSubmit = () => {
    onConfirm(manufacturer.id, selectedGlns, excludeWithdrawn);
  };

  if (!manufacturer) return null;

  const counts = preview?.counts;
  const nothingToImport = counts && counts.to_import === 0;

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Re-import {manufacturer.name} from S3 XML</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group>
            <Form.Label>Select GLNs</Form.Label>
            <div className="mt-2">
              {manufacturer.manufacturer_glns &&
                Object.entries(manufacturer.manufacturer_glns).map(([gln, subCompanyName]) => (
                  <Form.Check
                    type="checkbox"
                    key={gln}
                    id={`reimport-gln-${gln}`}
                    label={`${gln} ${subCompanyName ? `(${subCompanyName})` : ''}`}
                    name="reimportGlnOptions"
                    value={gln}
                    checked={selectedGlns.includes(gln)}
                    onChange={() => handleGlnChange(gln)}
                    className="mb-2"
                  />
                ))}
            </div>
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Check
              type="checkbox"
              id="reimport-exclude-withdrawn"
              label="Exclude withdrawn products (don't revive archived products)"
              checked={excludeWithdrawn}
              onChange={() => setExcludeWithdrawn(prev => !prev)}
            />
          </Form.Group>
        </Form>

        <div className="mt-3">
          <Form.Label>Plan preview</Form.Label>
          {previewLoading ? (
            <div className="text-muted">
              <Spinner animation="border" size="sm" className="mr-2" />
              Building plan…
            </div>
          ) : counts ? (
            <div>
              <div className="text-muted mb-2">
                {preview.total_files} file(s) found for the selected GLN(s)
              </div>
              <Badge variant="success" className="mr-2">
                {counts.to_import} to import
              </Badge>
              <Badge variant="secondary" className="mr-2">
                {counts.skipped_withdrawn} skipped (withdrawn)
              </Badge>
              <Badge variant="warning">{counts.skipped_superseded} skipped (superseded)</Badge>
              {preview.truncated_glns && preview.truncated_glns.length > 0 && (
                <div className="text-warning mt-2">
                  Note: {preview.truncated_glns.length} GLN(s) exceed the file-listing cap; the plan
                  may be partial.
                </div>
              )}
            </div>
          ) : selectedGlns.length === 0 ? (
            <div className="text-muted">Select at least one GLN.</div>
          ) : previewError ? (
            <div className="text-danger">{previewError}</div>
          ) : (
            <div className="text-muted">No preview available.</div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={
            loading || previewLoading || selectedGlns.length === 0 || !counts || nothingToImport
          }
        >
          {loading ? 'Queuing…' : 'Re-import'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

GDSNReimportFromS3Modal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  manufacturer: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default GDSNReimportFromS3Modal;
